import React, { useEffect, useMemo, useState } from "react";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { getDriverDailyAttendance, getDriverMonthlyAttendance, getDrivers, markAttendance } from "../services/drivers";
import Spinner from '../components/ui/Spinner';
import MultiSelectDropdown from '../components/ui/MultiSelectDropdown';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import { formatDate, formatDMY } from "../utils/datetime";
import { useBranches } from '../context/branches';
import { USER_ROLE } from '../services/auth';
import { toast } from 'react-toastify';

const ATTENDANCE_TABS = ['all', 'present', 'absent', 'halfday', 'overtime'];
const DAILY_STATUS_KEYS = ['present', 'absent', 'halfday', 'overtime'];

const DriverAttendancePage = ({ driver, onBack }) => {
  const role = localStorage.getItem(USER_ROLE);
  const showBranches = role === 'admin';
  const canMarkNonPresent = ['admin', 'branch_admin'].includes(role);
  const openedFromList = !!onBack;

  const today = new Date();
  const [drivers, setDrivers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(showBranches ? '' : (driver?.branch?.id || ''));
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMode, setViewMode] = useState('daily');

  const [attendanceData, setAttendanceData] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [monthlyMeta, setMonthlyMeta] = useState({
    total_working_days: null,
    completed_working_days: null,
  });
  const [activeTab, setActiveTab] = useState('all');

  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Confirm Attendance Action',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  let branches = [];
  if (showBranches) {
    const result = useBranches({ reload: true, params: { status: 'active', paginate: false } });
    branches = result.branches;
  }

  useEffect(() => {
    if (!showBranches) {
      fetchDrivers(driver?.branch?.id || 0);
    }
  }, []);

  useEffect(() => {
    if (!showBranches) return;
    if (!branches?.length) return;

    const initialBranch = driver?.branch?.id || branches[0]?.id || '';
    setSelectedBranch(initialBranch);
    fetchDrivers(initialBranch);
  }, [branches]);

  useEffect(() => {
    if (!selectedDrivers.length) {
      setAttendanceData([]);
      setMonthlyRows([]);
      setMonthlyMeta({ total_working_days: null, completed_working_days: null });
      return;
    }
    if (viewMode === 'daily') {
      loadAttendanceForDate(selectedDate);
    } else {
      loadMonthlyReport(selectedDate);
    }
  }, [selectedDrivers, selectedDate, viewMode]);

  const fetchDrivers = async (branch) => {
    setLoadingPage(true);
    try {
      const [err, data] = await getDrivers({
        paginate: false,
        branch: showBranches ? (branch || 0) : 0,
        status: 'active',
      });

      if (err) {
        toast.error('Failed to load drivers.');
        setDrivers([]);
        setSelectedDrivers([]);
        return;
      }

      const list = data?.results || (Array.isArray(data) ? data : []);
      setDrivers(list);
      // Default: all drivers selected
      setSelectedDrivers(list.map((d) => d.id));
    } catch (e) {
      console.error('Fetch drivers exception:', e);
      toast.error('Failed to load drivers.');
    } finally {
      setLoadingPage(false);
    }
  };

  const onBranchChange = async (e) => {
    const branchId = e.target.value;
    setSelectedBranch(branchId);
    setAttendanceData([]);
    setMonthlyRows([]);
    setMonthlyMeta({ total_working_days: null, completed_working_days: null });
    await fetchDrivers(branchId);
  };

  const normalizeAttendanceStatus = (statusValue) => {
    const value = (statusValue || '').toString().toLowerCase();
    if (!value) return '';
    if (value === 'halfday' || value === 'half_day' || value === 'half-day' || value === 'halftime') {
      return 'halfday';
    }
    if (value === 'overtime' || value === 'over_time' || value === 'over-time') {
      return 'overtime';
    }
    if (value === 'absent') return 'absent';
    if (value === 'present') return 'present';
    return '';
  };

  const resolveDriverStatus = (sessions) => {
    if (!sessions?.length) return '';

    const normalized = sessions
      .map((session) => normalizeAttendanceStatus(session?.attendance_type || session?.status))
      .filter(Boolean);

    if (normalized.includes('absent')) return 'absent';
    if (normalized.includes('halfday')) return 'halfday';
    if (normalized.includes('overtime')) return 'overtime';

    const hasOvertimeFlag = sessions.some((session) => session?.is_overtime || session?.overtime);
    if (hasOvertimeFlag) return 'overtime';

    return '';
  };

  const summarizeTime = (sessions) => {
    const withCheckIn = sessions.find((s) => s?.check_in_time);
    const withCheckOut = [...sessions].reverse().find((s) => s?.check_out_time);

    return {
      in: withCheckIn?.check_in_time ? new Date(withCheckIn.check_in_time).toLocaleTimeString() : '-',
      out: withCheckOut?.check_out_time ? new Date(withCheckOut.check_out_time).toLocaleTimeString() : '-',
    };
  };

  const normalizeAttendanceRecord = (item, idx = 0) => {
    const driverObj = item?.driver;
    const driverId = Number(driverObj?.id || item?.driver_id || (selectedDrivers[idx] || idx + 1));
    const localDriver = drivers.find((d) => d.id === driverId);
    const sessions = Array.isArray(item?.sessions) ? item.sessions : [];
    const summary = summarizeTime(sessions);
    const statusFromAttendanceType = normalizeAttendanceStatus(item?.attendance_type);
    const statusFromSessions = resolveDriverStatus(sessions);
    const hasAttendanceTypeKey = Object.prototype.hasOwnProperty.call(item || {}, 'attendance_type');
    const rawAttendanceType = (item?.attendance_type ?? '').toString().trim();
    const status = hasAttendanceTypeKey
      ? (rawAttendanceType ? statusFromAttendanceType : '')
      : statusFromSessions;

    return {
      driver_id: driverId,
      driver_name: driverObj?.name || localDriver?.name || '-',
      branch_name: driverObj?.branch?.name || localDriver?.branch?.name || '-',
      date: item?.date || '-',
      total_hours: Number(item?.total_hours || 0),
      status,
      check_in: summary.in,
      check_out: summary.out,
      sessions,
    };
  };

  const normalizeDailyRecords = (response) => {
    const payload = (response && typeof response === 'object' && 'data' in response)
      ? response.data
      : response;
    if (!payload) return [];

    if (!Array.isArray(payload)) {
      const hasStatusBuckets = DAILY_STATUS_KEYS.some((key) => Array.isArray(payload?.[key]));
      if (hasStatusBuckets) {
        const rows = [];
        DAILY_STATUS_KEYS.forEach((statusKey) => {
          const list = payload?.[statusKey] || [];
          list.forEach((item) => {
            rows.push(normalizeAttendanceRecord({ ...item, attendance_type: item?.attendance_type || statusKey }));
          });
        });
        return rows;
      }
      return [];
    }

    return payload.map((item, idx) => normalizeAttendanceRecord(item, idx));
  };

  const loadAttendanceForDate = async (date) => {
    try {
      if (!selectedDrivers.length) {
        setAttendanceData([]);
        return;
      }

      setLoadingAttendance(true);
      const dt = formatDate(date);
      const [err, response] = await getDriverDailyAttendance(selectedDrivers, dt);
      if (err) {
        setAttendanceData([]);
        return;
      }
      setAttendanceData(normalizeDailyRecords(response));
    } catch (e) {
      console.error('Error loading attendance for date:', e);
      toast.error('Failed to load attendance records.');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const loadMonthlyReport = async (date) => {
    try {
      if (!selectedDrivers.length) {
        setMonthlyRows([]);
        return;
      }
      setLoadingAttendance(true);
      const parts = formatDate(date).split('-');
      const month = Number(parts[1]);
      const year = Number(parts[0]);

      const [err, response] = await getDriverMonthlyAttendance(selectedDrivers, month, year);
      if (err) {
        setMonthlyRows([]);
        setMonthlyMeta({ total_working_days: null, completed_working_days: null });
        return;
      }

      const payload = (response && typeof response === 'object' && 'data' in response)
        ? response.data
        : response;
      const rows = Array.isArray(payload?.report) ? payload.report : [];
      setMonthlyMeta({
        total_working_days: payload?.total_working_days ?? null,
        completed_working_days: payload?.completed_working_days ?? null,
      });
      setMonthlyRows(rows.map((item, idx) => ({
        driver_id: item?.driver_id || item?.driver?.id || idx + 1,
        driver_name: item?.driver_name || '-',
        branch_name: item?.branch || '-',
        row_total_working_days: item?.total_working_days ?? null,
        present_days: Number(item?.present_days || 0),
        absent_days: Number(item?.absent_days || 0),
        halfdays: Number(item?.halfdays || 0),
        overtime: Number(item?.overtime || 0),
      })));
    } catch (e) {
      console.error('Error loading monthly report:', e);
      toast.error('Failed to load monthly report.');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const markForSelectedDrivers = async (attendanceType) => {
    if (!selectedDrivers.length) {
      toast.error('Select at least one driver.');
      return;
    }

    try {
      const dt = formatDate(selectedDate);
      setLoadingAttendance(true);

      const [err] = await markAttendance(selectedDrivers[0], dt, attendanceType, selectedDrivers);
      if (err) {
        toast.error('Failed to mark attendance.');
        return;
      }

      toast.success(`Marked ${attendanceType} for selected driver(s).`);
      await loadAttendanceForDate(selectedDate);
    } catch (e) {
      console.error('Error marking attendance:', e);
      toast.error('Failed to mark attendance.');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const openMarkConfirmation = (attendanceType) => {
    const count = selectedDrivers.length;
    const prettyType = attendanceType === 'halfday' ? 'halfday' : attendanceType;
    setConfirmModalParams({
      open: true,
      title: 'Confirm Attendance Action',
      message: `Mark ${prettyType} for ${count} selected driver${count > 1 ? 's' : ''} on ${formatDMY(formatDate(selectedDate))}?`,
      onConfirm: async () => {
        await markForSelectedDrivers(attendanceType);
        setConfirmModalParams((prev) => ({ ...prev, open: false }));
      },
      confirmText: 'Yes, Mark',
      cancelText: 'Cancel',
    });
  };

  const tabCounts = useMemo(() => ({
    all: attendanceData.length,
    present: attendanceData.filter((row) => row.status === 'present').length,
    absent: attendanceData.filter((row) => row.status === 'absent').length,
    halfday: attendanceData.filter((row) => row.status === 'halfday').length,
    overtime: attendanceData.filter((row) => row.status === 'overtime').length,
  }), [attendanceData]);

  const filteredAttendance = useMemo(() => {
    if (activeTab === 'all') return attendanceData;
    return attendanceData.filter((row) => row.status === activeTab);
  }, [attendanceData, activeTab]);

  const handleBack = () => {
    if (onBack) onBack();
  };

  if (loadingPage) return <Spinner />;

  return (
    <div className="p-4 uppercase [&_select]:uppercase [&_input[type='text']]:uppercase [&_input:disabled]:uppercase">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Driver Attendance</h2>
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1 rounded text-sm border ${viewMode === 'daily' ? 'bg-[#293686] text-white border-[#293686]' : 'bg-white text-gray-700'}`}
            onClick={() => setViewMode('daily')}
          >
            Daily View
          </button>
          <button
            className={`px-3 py-1 rounded text-sm border ${viewMode === 'monthly' ? 'bg-[#293686] text-white border-[#293686]' : 'bg-white text-gray-700'}`}
            onClick={() => setViewMode('monthly')}
          >
            Monthly View
          </button>
          {openedFromList && (
            <button
              className="text-sm font-medium text-[#293686] border border-[#D8E0FF] bg-white hover:bg-[#F3F6FF] px-3 py-1.5 rounded-md transition-colors"
              onClick={handleBack}
            >
              {'< BACK TO LIST'}
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        {showBranches && (
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Branch</label>
            <select
              value={selectedBranch}
              onChange={onBranchChange}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className={showBranches ? 'md:col-span-2' : 'md:col-span-3'}>
          <MultiSelectDropdown
            label="Driver"
            placeholder="Select Driver"
            options={drivers}
            selectedValues={selectedDrivers}
            onChange={setSelectedDrivers}
            getOptionLabel={(item) => (item?.name || '-').toUpperCase()}
            getOptionValue={(item) => item?.id}
            showSelectAll={true}
            selectAllLabel="Select All Drivers"
            disabled={drivers.length === 0}
          />
        </div>
      </div>

      <div className="flex gap-8 flex-col lg:flex-row">
        <div className="w-full lg:w-1/3 min-w-[280px] border rounded-lg p-4 bg-white">
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            onClickDay={viewMode === 'daily' ? loadAttendanceForDate : undefined}
            view={viewMode === 'monthly' ? 'year' : 'month'}
            minDetail={viewMode === 'monthly' ? 'year' : 'month'}
            maxDetail={viewMode === 'monthly' ? 'year' : 'month'}
          />

          {viewMode === 'daily' && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                className="w-full bg-green-600 hover:bg-green-700 text-white rounded py-2 text-sm font-medium"
                onClick={() => openMarkConfirmation('present')}
              >
                Mark Present
              </button>

              {canMarkNonPresent && (
                <button
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded py-2 text-sm font-medium"
                  onClick={() => openMarkConfirmation('absent')}
                >
                  Mark Absent
                </button>
              )}

              {canMarkNonPresent && (
                <button
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded py-2 text-sm font-medium"
                  onClick={() => openMarkConfirmation('halfday')}
                >
                  Mark Halftime
                </button>
              )}

              {canMarkNonPresent && (
                <button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded py-2 text-sm font-medium"
                  onClick={() => openMarkConfirmation('overtime')}
                >
                  Mark Overtime
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            {viewMode === 'daily'
              ? `For selected date: ${formatDMY(formatDate(selectedDate))}`
              : `For selected month: ${selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}`}
          </p>
        </div>

        <div className="flex-1 border rounded-lg p-4 bg-white">
          <h3 className="text-lg font-semibold mb-3">
            {viewMode === 'daily'
              ? `Attendance for ${formatDMY(formatDate(selectedDate))}`
              : `Monthly Report for ${selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}`}
          </h3>

          {viewMode === 'daily' && (
            <div className="flex flex-wrap gap-2 mb-3">
              {ATTENDANCE_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`px-3 py-1 rounded text-sm border ${activeTab === tab ? 'bg-[#293686] text-white border-[#293686]' : 'bg-white text-gray-700'}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {(tab === 'halfday' ? 'Halfday' : (tab[0].toUpperCase() + tab.slice(1)))} ({tabCounts[tab] || 0})
                </button>
              ))}
            </div>
          )}

          {loadingAttendance
            ? <Spinner />
            : (viewMode === 'daily'
              ? <AttendanceTable records={filteredAttendance} />
              : <MonthlyAttendanceTable records={monthlyRows} monthlyMeta={monthlyMeta} />)}
        </div>
      </div>
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams((prev) => ({ ...prev, open: false }))}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

const AttendanceTable = ({ records }) => {
  if (!records.length) {
    return <div className="text-gray-600 mt-6">NO ATTENDANCE RECORDS FOR THIS DATE.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto mt-2 border">
        <thead>
          <tr className="bg-gray-100 text-sm">
            <th className="py-2 px-4 text-left border">Driver</th>
            <th className="py-2 px-4 text-left border">Branch</th>
            <th className="py-2 px-4 text-left border">Status</th>
            <th className="py-2 px-4 text-left border">Check In</th>
            <th className="py-2 px-4 text-left border">Check Out</th>
          </tr>
        </thead>
        <tbody>
          {records.map((rec, idx) => (
            <tr className="border-t text-sm" key={`${rec.driver_id}-${idx}`}>
              <td className="py-2 px-4 border">{rec.driver_name}</td>
              <td className="py-2 px-4 border">{rec.branch_name}</td>
              <td className="py-2 px-4 border uppercase">{rec.status || ''}</td>
              <td className="py-2 px-4 border">{rec.check_in}</td>
              <td className="py-2 px-4 border">{rec.check_out}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyAttendanceTable = ({ records, monthlyMeta }) => {
  if (!records.length) {
    return <div className="text-gray-600 mt-6">NO MONTHLY ATTENDANCE RECORDS FOUND.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto mt-2 border">
        <thead>
          <tr className="bg-gray-100 text-sm">
            <th className="py-2 px-4 text-left border">Driver</th>
            <th className="py-2 px-4 text-left border">Branch</th>
            <th className="py-2 px-4 text-left border">Completed Working Days</th>
            <th className="py-2 px-4 text-left border">Present Days</th>
            <th className="py-2 px-4 text-left border">Absent Days</th>
            <th className="py-2 px-4 text-left border">Halfday Days</th>
            <th className="py-2 px-4 text-left border">Overtime Days</th>
          </tr>
        </thead>
        <tbody>
          {records.map((rec, idx) => (
            <tr className="border-t text-sm" key={`${rec.driver_id}-${idx}`}>
              <td className="py-2 px-4 border">{rec.driver_name}</td>
              <td className="py-2 px-4 border">{rec.branch_name}</td>
              <td className="py-2 px-4 border">{monthlyMeta?.completed_working_days ?? rec.row_total_working_days ?? '-'}</td>
              <td className="py-2 px-4 border">{rec.present_days}</td>
              <td className="py-2 px-4 border">{rec.absent_days}</td>
              <td className="py-2 px-4 border">{rec.halfdays}</td>
              <td className="py-2 px-4 border">{rec.overtime}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DriverAttendancePage;
